// Per-channel images for Social Media AI posts. Each channel (Facebook, Instagram, ...) can carry
// its own image in the post's `channel_media` map; a channel without one uses the main image.
// Mirrors CompanyPost::CHANNEL_MEDIA_PLATFORMS on the Rails side. To add a network, add an entry
// here (and its publisher on the server).

// `cropPresets` is the Uploadcare widget v3 `crop` setting. Uploadcare ratios must be integers,
// so Instagram's widest legal shape (1.91:1) is written 191:100.
export const SOCIAL_CHANNELS = [
  { key: 'facebook', label: 'Facebook', cropPresets: 'free, 1:1, 4:5, 191:100', framedByServer: false },
  { key: 'instagram', label: 'Instagram', cropPresets: '4:5, 1:1, 191:100', framedByServer: true },
];

export const channelConfig = (key) => SOCIAL_CHANNELS.find((c) => c.key === key) || null;

// Which channels a post goes to, from its "Post on" value ('facebook' | 'instagram' | 'both').
export const channelsForTargets = (targets) => {
  if (targets === 'facebook') return ['facebook'];
  if (targets === 'instagram') return ['instagram'];
  return SOCIAL_CHANNELS.map((c) => c.key);
};

export const channelOverride = (channelMedia, key) => {
  const entry = channelMedia && channelMedia[key];
  return entry && entry.url ? entry : null;
};

// The image a channel publishes before any server-side framing.
export const channelSourceUrl = (channelMedia, key, mainUrl) => channelOverride(channelMedia, key)?.url || mainUrl || null;

export const withChannelOverride = (channelMedia, key, url, mediaType = 'image/jpeg') => ({
  ...(channelMedia || {}),
  [key]: { url, media_type: mediaType },
});

export const withoutChannelOverride = (channelMedia, key) => {
  const next = { ...(channelMedia || {}) };
  delete next[key];
  return next;
};

// Channels holding their own image but not selected in "Post on". The image is kept, not deleted.
export const parkedOverrides = (channelMedia, targets) => {
  const active = channelsForTargets(targets);
  return SOCIAL_CHANNELS.filter((c) => !active.includes(c.key) && channelOverride(channelMedia, c.key));
};

// Plain-language note under a channel's preview. `adjustment` comes from the server's
// /company_posts/instagram_preview (InstagramImageFraming) for Instagram.
export const channelPreviewNote = (key, adjustment) => {
  if (key === 'facebook') return { tone: 'info', text: 'Facebook posts this image exactly as shown.' };

  switch (adjustment) {
    case 'pad_wide':
      return {
        tone: 'warn',
        text: 'This image is wider than Instagram allows, so Instagram will get it with soft blurred bars above and below, as shown. To avoid the bars, adjust the crop or upload a different image for Instagram.',
      };
    case 'pad_tall':
    case 'unmeasured':
      return {
        tone: 'warn',
        text: 'This image is taller than Instagram allows, so Instagram will get it with soft blurred bars on the left and right, as shown. To avoid the bars, adjust the crop or upload a different image for Instagram.',
      };
    case 'trim_wide':
      return { tone: 'info', text: 'This image is a little wider than Instagram allows, so a thin strip is trimmed from each side, as shown.' };
    case 'trim_tall':
      return { tone: 'info', text: 'This image is a little taller than Instagram allows, so a thin strip is trimmed from the top and bottom, as shown.' };
    default:
      return { tone: 'info', text: 'Instagram posts this image exactly as shown.' };
  }
};

// The 'facebook' | 'instagram' | 'both' value for a set of picked account keys
// ("facebook:<id>" / "instagram:<id>"), so previews and the Instagram image check follow the pick.
export const targetsForAccountKeys = (keys) => {
  const platforms = new Set((keys || []).map((k) => String(k).split(':')[0]));
  if (platforms.has('facebook') && platforms.has('instagram')) return 'both';
  if (platforms.has('instagram')) return 'instagram';
  if (platforms.has('facebook')) return 'facebook';
  return 'both';
};
